/**
 * 组件点击时统一的数据提取与打包流程
 * 每个组件都走此函数：按 componentType 做 case，组织 current + 7天/30天/12月，再交给钩子发给 AI
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { storeComponentUsesTimeDirection } from './storeComponentBundle';
/**
 * 从房态 dataSource 中计算价格建议上下文：整体出租率、剩余总房数、当前时间等
 * 与动态定价提示词（外部因素、订单流速、追随机制、房型策略）配套使用
 */
function buildPriceSuggestionContext(dataSource, storeData) {
    var _a, _b, _c, _d, _e;
    if (!Array.isArray(dataSource) || dataSource.length === 0)
        return undefined;
    var now = new Date();
    var hour = now.getHours();
    var minute = now.getMinutes();
    var storeTotalRow = dataSource.find(function (r) { return r.isStoreTotal; });
    var overallOccupancy = storeTotalRow == null ? 0 : Number(storeTotalRow.progress) || 0;
    var remainingTotalRooms = storeTotalRow == null ? 0 : Number(storeTotalRow.quantityOpen) || 0;
    var orderFlowSpeed = 'normal';
    var raw = (_d = (_b = (_a = storeData.orderTrafficBundle) === null || _a === void 0 ? void 0 : _a.last24Hours) !== null && _b !== void 0 ? _b : (_c = storeData.orderTraffic) === null || _c === void 0 ? void 0 : _c.rawData) !== null && _d !== void 0 ? _d : storeData.orderTraffic;
    if (raw && typeof raw === 'object') {
        var orders = (_e = raw.orders) !== null && _e !== void 0 ? _e : raw.orderCount;
        if (Array.isArray(orders) && orders.length > 0) {
            var recent = orders.slice(-3).reduce(function (s, v) { return s + (Number(v) || 0); }, 0);
            var prev = orders.slice(-6, -3).reduce(function (s, v) { return s + (Number(v) || 0); }, 0);
            if (recent > prev * 1.2)
                orderFlowSpeed = 'fast';
            else if (prev > 0 && recent < prev * 0.8)
                orderFlowSpeed = 'slow';
        }
    }
    return {
        currentTime: "".concat(String(hour).padStart(2, '0'), ":").concat(String(minute).padStart(2, '0')),
        overallOccupancy: overallOccupancy,
        remainingTotalRooms: remainingTotalRooms,
        orderFlowSpeed: orderFlowSpeed,
    };
}
/**
 * 按组件类型从 storeData 中提取并打包数据。
 * 内置多档包组件勿带 timeDirection（current 已含 last7Days 等键）。
 */
export function buildComponentDataPackage(storeId, componentType, storeData, timeDirection) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    if (timeDirection === void 0) { timeDirection = 'past'; }
    var includeTimeDirection = storeComponentUsesTimeDirection(componentType);
    var emptyPackage = function (current, extra) { return (__assign(__assign(__assign({}, (includeTimeDirection ? { timeDirection: timeDirection } : {})), { current: current, last7Days: undefined, last30Days: undefined, last12Months: undefined }), extra)); };
    switch (componentType) {
        case 'room-status': {
            var dataSource = (_b = (_a = storeData.roomStatus) === null || _a === void 0 ? void 0 : _a.dataSource) !== null && _b !== void 0 ? _b : null;
            var priceSuggestionContext = buildPriceSuggestionContext(dataSource, storeData);
            return emptyPackage(dataSource, priceSuggestionContext ? { priceSuggestionContext: priceSuggestionContext } : undefined);
        }
        case 'order-traffic':
            return emptyPackage((_c = storeData.orderTrafficBundle) !== null && _c !== void 0 ? _c : null);
        case 'room-status-recently':
            return emptyPackage((_e = (_d = storeData.roomStatusRecently) === null || _d === void 0 ? void 0 : _d.baseChartData) !== null && _e !== void 0 ? _e : null);
        case 'price-alert':
            return emptyPackage((_f = storeData === null || storeData === void 0 ? void 0 : storeData.priceAlert) !== null && _f !== void 0 ? _f : null);
        case 'revpar':
            return emptyPackage((_g = storeData.revparBundle) !== null && _g !== void 0 ? _g : null);
        case 'channel-info':
            return emptyPackage((_h = storeData.channelInfoBundle) !== null && _h !== void 0 ? _h : null);
        case 'channel-price':
            return emptyPackage((_k = (_j = storeData.channelPrice) === null || _j === void 0 ? void 0 : _j.rawData) !== null && _k !== void 0 ? _k : null);
        case 'room-health':
            return emptyPackage((_l = storeData.roomHealthBundle) !== null && _l !== void 0 ? _l : null);
        case 'customer-source':
            return emptyPackage((_m = storeData.customerSourceBundle) !== null && _m !== void 0 ? _m : null);
        case 'discount-analysis':
            return emptyPackage((_o = storeData.discountAnalysisBundle) !== null && _o !== void 0 ? _o : null);
        case 'channel-analysis':
            return emptyPackage((_p = storeData.channelAnalysisBundle) !== null && _p !== void 0 ? _p : null);
        case 'occupancy-analysis':
            return emptyPackage((_q = storeData.occupancyAnalysisBundle) !== null && _q !== void 0 ? _q : null);
        case 'operation-efficiency':
            return emptyPackage((_r = storeData.operationEfficiencyBundle) !== null && _r !== void 0 ? _r : null);
        case 'store-overview':
            return emptyPackage((_s = storeData.storeOverview) !== null && _s !== void 0 ? _s : null);
        default:
            return emptyPackage(null);
    }
}
